// Parent sign-up. Standalone card (renders even when unauthenticated — it's a
// PUBLIC_CHILD_PATH in the locale gate). On success we land the new parent in
// their shell by navigating to the locale root, which the gate now renders as
// the authed shell.

import { useLocale, useT } from '@fajrarabi/i18n/react';
import { Alert, BrandLockup, Button, Field, Input } from '@fajrarabi/ui';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { LangSwitcher } from '~/components/LangSwitcher';
import { ApiError } from '~/lib/api';
import { register, useAuth } from '~/lib/auth';
import { asLocale } from '~/lib/locale';

export const Route = createFileRoute('/$locale/signup')({
  component: SignUpPage,
});

function SignUpPage() {
  const t = useT();
  const { isRtl } = useLocale();
  const navigate = useNavigate();
  const { locale: rawLocale } = useParams({ from: '/$locale/signup' });
  const locale = asLocale(rawLocale);
  const { refresh } = useAuth();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await register({ email, password, displayName, locale, marketingOptIn });
      await refresh();
      await navigate({ to: '/$locale', params: { locale } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(t('app.common.error_generic'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-dvh bg-cream-50">
      <header className="flex items-center justify-between px-6 py-5">
        <BrandLockup label={t('app.common.app_name')} />
        <LangSwitcher locale={locale} ariaLabel={t('app.common.lang_switch_label')} />
      </header>

      <main className="grid place-items-center px-6 pb-16 pt-6">
        <div className="w-full max-w-md">
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl bg-white p-8 shadow-[0_24px_60px_-30px_rgba(31,26,23,0.18)] ring-1 ring-burgundy-500/10"
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            <h1 className="text-2xl font-bold text-ink-900">{t('app.auth.signup_title')}</h1>
            <p className="mt-1 text-sm text-ink-700">{t('app.auth.signup_subtitle')}</p>

            <div className="mt-6 space-y-4">
              <Field label={t('app.auth.name_label')}>
                {(id) => (
                  <Input
                    id={id}
                    type="text"
                    required
                    autoComplete="name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('app.auth.email_label')}>
                {(id) => (
                  <Input
                    id={id}
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('app.auth.password_label')} hint={t('app.auth.password_hint_parent')}>
                {(id) => (
                  <Input
                    id={id}
                    type="password"
                    required
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                )}
              </Field>

              <label className="flex items-start gap-3 text-sm text-ink-700">
                <input
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={(e) => setMarketingOptIn(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-ink-500/30 text-burgundy-500 focus:ring-burgundy-500"
                />
                <span>{t('app.auth.marketing_opt_in')}</span>
              </label>
            </div>

            {error && (
              <Alert tone="danger" className="mt-4">
                {error}
              </Alert>
            )}

            <Button type="submit" loading={submitting} fullWidth className="mt-6">
              {t('app.auth.create_account')}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-ink-700">
            {t('app.auth.have_account')}{' '}
            <Link
              to="/$locale"
              params={{ locale }}
              className="font-semibold text-burgundy-500 hover:text-burgundy-600"
            >
              {t('app.auth.sign_in_link')}
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
